// Shared Admin Components
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ══════════════════════════════════════════
// COLLAPSIBLE SECTION
// ══════════════════════════════════════════

export const CollapsibleSection = ({ title, icon, statusBadge, warningBadge, children, defaultExpanded = false, dimmed = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div style={{ 
      background: '#fff', 
      border: '1px solid #e0d5c5', 
      marginBottom: '20px',
      opacity: dimmed ? 0.7 : 1
    }}>
      <div 
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ 
          padding: '16px 20px', 
          background: '#f5efe6', 
          borderBottom: isExpanded ? '1px solid #e0d5c5' : 'none',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <span style={{ fontFamily: 'Cinzel, serif', fontSize: '1rem', color: '#2E221A', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {icon && <span>{icon}</span>}
          {title}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {warningBadge && (
            <span style={{ padding: '4px 12px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(198,40,40,0.15)', color: '#C62828' }}>
              {warningBadge}
            </span>
          )}
          {statusBadge && (
            <span style={{ padding: '4px 12px', fontSize: '0.75rem', fontWeight: '600', background: statusBadge.bg, color: statusBadge.color }}>
              {statusBadge.text}
            </span>
          )}
          <span style={{ 
            fontSize: '1.2rem', 
            color: '#8B4513',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            ▼
          </span>
        </div>
      </div>
      {isExpanded && (
        <div style={{ padding: '20px' }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════
// PAST COMPLETED ORDERS SECTION
// ══════════════════════════════════════════

export const PastCompletedOrdersSection = ({ customerId, customerPhone, currentOrderId }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  useEffect(() => {
    const fetchPastOrders = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${API_URL}/api/admin/customer-past-orders/${customerId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setOrders(data.orders.filter(o => o.order_id !== currentOrderId));
        }
      } catch (err) {
        console.error('Error fetching past orders:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPastOrders();
  }, [customerId, currentOrderId]);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + ', ' +
           date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const getServiceLabel = (type) => {
    const labels = {
      'astro_analysis': 'Astro Analysis',
      'anusthan': 'Anusthan',
      'bhoj_daan': 'Bhoj & Daan'
    };
    return labels[type] || type;
  };

  return (
    <CollapsibleSection
      title="List of Past Completed Orders by this Customer"
      icon="📜"
      statusBadge={orders.length > 0 ? { text: `${orders.length} Order(s)`, color: '#2E7D32', bg: 'rgba(76,175,80,0.15)' } : null}
      defaultExpanded={false}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: '#725b47' }}>Loading past orders...</div>
      ) : orders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px', color: '#725b47' }}>
          No past completed orders found for this customer
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: '16px', fontSize: '0.85rem', color: '#725b47', padding: '12px', background: '#faf8f5', border: '1px solid #e0d5c5' }}>
            <strong>Customer ID:</strong> {customerId} | <strong>Mobile:</strong> {customerPhone}
          </div>
          
          {orders.map((order) => (
            <div key={order.order_id} style={{ marginBottom: '16px', border: '1px solid #e0d5c5', background: '#fff' }}>
              <div 
                onClick={() => setExpandedOrderId(expandedOrderId === order.order_id ? null : order.order_id)}
                style={{ 
                  padding: '14px 16px', 
                  background: '#f5efe6', 
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#8B4513' }}>{order.order_id}</span>
                  <span style={{ fontSize: '0.85rem', color: '#2E221A' }}>{formatDateTime(order.created_at)}</span>
                  <span style={{ padding: '3px 10px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(139,69,19,0.15)', color: '#8B4513' }}>
                    {getServiceLabel(order.service_type)}
                  </span>
                  <span style={{ padding: '3px 8px', fontSize: '0.7rem', fontWeight: '600', background: 'rgba(76,175,80,0.15)', color: '#2E7D32' }}>
                    Completed
                  </span>
                </div>
                <span style={{ 
                  fontSize: '1rem', 
                  color: '#8B4513',
                  transition: 'transform 0.2s',
                  transform: expandedOrderId === order.order_id ? 'rotate(180deg)' : 'rotate(0deg)'
                }}>
                  ▼
                </span>
              </div>
              
              {expandedOrderId === order.order_id && (
                <div style={{ padding: '16px' }}>
                  {order.service_type === 'astro_analysis' && (
                    <div>
                      <div style={{ marginBottom: '16px', padding: '12px', background: '#f0f7ff', border: '1px solid #bbdefb' }}>
                        <div style={{ fontSize: '0.75rem', color: '#1565C0', textTransform: 'uppercase', marginBottom: '8px', fontWeight: '600' }}>
                          📄 Astro Analysis Document
                        </div>
                        {order.astro_analysis_file ? (
                          <a href={order.astro_analysis_file} target="_blank" rel="noopener noreferrer" style={{ color: '#1565C0', textDecoration: 'underline' }}>
                            View Document
                          </a>
                        ) : (
                          <span style={{ color: '#757575' }}>No document uploaded</span>
                        )}
                      </div>
                      
                      <div style={{ padding: '12px', background: '#fff8e1', border: '1px solid #ffecb3' }}>
                        <div style={{ fontSize: '0.75rem', color: '#E65100', textTransform: 'uppercase', marginBottom: '8px', fontWeight: '600' }}>
                          💎 Bracelet Configuration
                        </div>
                        {order.bracelet_config && order.bracelet_config.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {order.bracelet_config.filter(s => s.count > 0).map((stone, idx) => (
                              <span key={idx} style={{ padding: '4px 10px', background: '#fff', border: '1px solid #e0d5c5', fontSize: '0.8rem' }}>
                                {stone.stone}: {stone.count}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: '#757575' }}>No bracelet configured</span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {order.service_type === 'anusthan' && (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #e0d5c5' }}>
                          <td style={{ padding: '8px 12px', fontSize: '0.75rem', color: '#725b47', textTransform: 'uppercase', width: '150px' }}>Anusthan Name</td>
                          <td style={{ padding: '8px 12px', fontSize: '0.9rem', color: '#8B4513', fontWeight: '500' }}>{order.anusthan_name || '-'}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e0d5c5' }}>
                          <td style={{ padding: '8px 12px', fontSize: '0.75rem', color: '#725b47', textTransform: 'uppercase' }}>Package</td>
                          <td style={{ padding: '8px 12px', fontSize: '0.9rem', color: '#2E221A' }}>
                            {order.package_name || '-'}
                            {order.package_price > 0 && <span style={{ marginLeft: '8px', color: '#2E7D32' }}>₹{order.package_price}</span>}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px 12px', fontSize: '0.75rem', color: '#725b47', textTransform: 'uppercase' }}>Video URL</td>
                          <td style={{ padding: '8px 12px', fontSize: '0.9rem' }}>
                            {order.anusthan_video_url ? (
                              <a href={order.anusthan_video_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1565C0', textDecoration: 'underline' }}>
                                🎥 Watch Video
                              </a>
                            ) : (
                              <span style={{ color: '#757575' }}>No video available</span>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </CollapsibleSection>
  );
};

// ══════════════════════════════════════════
// ANUSTHAN SCHEDULE SECTION
// ══════════════════════════════════════════

export const AnusthanScheduleSection = ({ order, onSchedule }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [scheduling, setScheduling] = useState(false);

  const getCurrentStatusLabel = () => {
    switch (order.status) {
      case 'new': return 'New';
      case 'pending': return 'Pending';
      case 'paid': return 'Paid';
      default: return order.status || 'Pending';
    }
  };

  const handleSchedule = async () => {
    if (!window.confirm('Are you sure you want to schedule this Anusthan? The order will be moved to "Scheduled Anusthans" tab.')) {
      return;
    }
    setScheduling(true);
    await onSchedule(order.order_id);
    setScheduling(false);
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #7B1FA2', marginBottom: '20px' }}>
      <div 
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ 
          padding: '16px 20px', 
          background: '#f3e5f5', 
          borderBottom: isExpanded ? '1px solid #ce93d8' : 'none',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <span style={{ fontFamily: 'Cinzel, serif', fontSize: '1rem', color: '#4A148C' }}>
          📅 Schedule Anusthan
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ padding: '4px 12px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(255,152,0,0.15)', color: '#E65100' }}>
            Current Status: {getCurrentStatusLabel()}
          </span>
          <span style={{ 
            fontSize: '1.2rem', 
            color: '#7B1FA2',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            ▼
          </span>
        </div>
      </div>
      {isExpanded && (
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '20px', padding: '16px', background: '#faf8f5', border: '1px solid #e0d5c5' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', color: '#725b47', textTransform: 'uppercase', marginBottom: '4px' }}>Anusthan</label>
                <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '1rem', color: '#7B1FA2', fontWeight: '600' }}>{order.anusthan_name || '-'}</div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', color: '#725b47', textTransform: 'uppercase', marginBottom: '4px' }}>Ceremony Date</label>
                <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '1rem', color: '#2E221A' }}>{order.anusthan_date || '-'}</div>
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#725b47', marginBottom: '8px' }}>
                Change Anusthan Status to:
              </label>
              <div style={{ padding: '10px 16px', background: '#e1bee7', color: '#4A148C', fontWeight: '600', display: 'inline-block' }}>
                ✓ Scheduled
              </div>
            </div>
            <button
              onClick={handleSchedule}
              disabled={scheduling}
              style={{
                padding: '12px 24px',
                fontFamily: 'Cinzel, serif',
                fontSize: '0.9rem',
                background: scheduling ? '#ccc' : '#7B1FA2',
                color: '#fff',
                border: 'none',
                cursor: scheduling ? 'not-allowed' : 'pointer',
                letterSpacing: '0.05em'
              }}
            >
              {scheduling ? 'Scheduling...' : '📅 Save & Schedule'}
            </button>
          </div>
          
          <p style={{ marginTop: '16px', fontSize: '0.8rem', color: '#7B1FA2', background: '#f3e5f5', padding: '12px', border: '1px dashed #ce93d8' }}>
            ℹ️ Clicking "Save & Schedule" will change the order status to "Scheduled" and move it to the "Scheduled Anusthans" tab.
          </p>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════
// BHOJ & DAAN SCHEDULE SECTION
// ══════════════════════════════════════════

export const BhojDaanScheduleSection = ({ order, onSchedule }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [scheduling, setScheduling] = useState(false);

  const getCurrentStatusLabel = () => {
    switch (order.status) {
      case 'new': return 'New';
      case 'pending': return 'Pending';
      case 'paid': return 'Paid';
      default: return order.status || 'Pending';
    }
  };

  const handleSchedule = async () => {
    if (!window.confirm('Are you sure you want to schedule this Bhoj & Daan? The order will be moved to "Scheduled Bhoj & Daan" tab.')) {
      return;
    }
    setScheduling(true);
    await onSchedule(order.order_id);
    setScheduling(false);
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #00695C', marginBottom: '20px' }}>
      <div 
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ 
          padding: '16px 20px', 
          background: '#e0f2f1', 
          borderBottom: isExpanded ? '1px solid #80cbc4' : 'none',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <span style={{ fontFamily: 'Cinzel, serif', fontSize: '1rem', color: '#004D40' }}>
          📅 Schedule Bhoj & Daan
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ padding: '4px 12px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(255,152,0,0.15)', color: '#E65100' }}>
            Current Status: {getCurrentStatusLabel()}
          </span>
          <span style={{ 
            fontSize: '1.2rem', 
            color: '#00695C',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            ▼
          </span>
        </div>
      </div>
      {isExpanded && (
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '20px', padding: '16px', background: '#faf8f5', border: '1px solid #e0d5c5' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', color: '#725b47', textTransform: 'uppercase', marginBottom: '4px' }}>Seva Types</label>
                <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#00695C', fontWeight: '500' }}>
                  {order.sub_service_types?.join(', ') || '-'}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', color: '#725b47', textTransform: 'uppercase', marginBottom: '4px' }}>Ceremony Date</label>
                <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '1rem', color: '#2E221A' }}>{order.ceremony_date || '-'}</div>
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#725b47', marginBottom: '8px' }}>
                Change Status to:
              </label>
              <div style={{ padding: '10px 16px', background: '#b2dfdb', color: '#004D40', fontWeight: '600', display: 'inline-block' }}>
                ✓ Scheduled
              </div>
            </div>
            <button
              onClick={handleSchedule}
              disabled={scheduling}
              style={{
                padding: '12px 24px',
                fontFamily: 'Cinzel, serif',
                fontSize: '0.9rem',
                background: scheduling ? '#ccc' : '#00695C',
                color: '#fff',
                border: 'none',
                cursor: scheduling ? 'not-allowed' : 'pointer',
                letterSpacing: '0.05em'
              }}
            >
              {scheduling ? 'Scheduling...' : '📅 Save & Schedule'}
            </button>
          </div>
          
          <p style={{ marginTop: '16px', fontSize: '0.8rem', color: '#00695C', background: '#e0f2f1', padding: '12px', border: '1px dashed #80cbc4' }}>
            ℹ️ Clicking "Save & Schedule" will change the order status to "Scheduled" and move it to the "Scheduled Bhoj & Daan" tab.
          </p>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════
// ASTRO ANALYSIS UPLOAD SECTION
// ══════════════════════════════════════════

export const AstroAnalysisUploadSection = ({ order, onUpload, onRemove, onStatusChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [file, setFile] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(order.astro_analysis_status || 'not_started');

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    await onUpload(order.order_id, file);
    setCurrentStatus('completed');
    setUploading(false);
    setFile(null);
  };

  const handleRemove = async () => {
    if (!window.confirm('Are you sure you want to remove the uploaded document? This will reset the status to "Analysis Not Started".')) {
      return;
    }
    setRemoving(true);
    await onRemove(order.order_id);
    setCurrentStatus('not_started');
    setRemoving(false);
  };

  const handleMarkInProgress = async () => {
    setUpdatingStatus(true);
    const success = await onStatusChange(order.order_id, 'in_progress');
    if (success) {
      setCurrentStatus('in_progress');
    }
    setUpdatingStatus(false);
  };

  const getStatusDisplay = () => {
    if (order.astro_analysis_file) {
      return { text: 'Astro Analysis is Done', color: '#2E7D32', bg: 'rgba(76,175,80,0.15)' };
    }
    switch (currentStatus) {
      case 'in_progress':
        return { text: 'Astro Analysis in Progress', color: '#1565C0', bg: 'rgba(33,150,243,0.15)' };
      case 'completed':
        return { text: 'Astro Analysis is Done', color: '#2E7D32', bg: 'rgba(76,175,80,0.15)' };
      default:
        return { text: 'Analysis Not Started', color: '#E65100', bg: 'rgba(255,152,0,0.15)' };
    }
  };

  const status = getStatusDisplay();

  return (
    <div style={{ background: '#fff', border: '1px solid #e0d5c5', marginBottom: '20px' }}>
      <div 
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ 
          padding: '16px 20px', 
          background: '#f5efe6', 
          borderBottom: isExpanded ? '1px solid #e0d5c5' : 'none',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <span style={{ fontFamily: 'Cinzel, serif', fontSize: '1rem', color: '#2E221A' }}>
          📄 Upload Astro Analysis Document
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ padding: '4px 12px', fontSize: '0.75rem', fontWeight: '600', background: status.bg, color: status.color }}>
            {status.text}
          </span>
          <span style={{ 
            fontSize: '1.2rem', 
            color: '#8B4513',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            ▼
          </span>
        </div>
      </div>
      {isExpanded && (
        <div style={{ padding: '20px' }}>
        {order.astro_analysis_file ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <span style={{ color: '#2E7D32' }}>✓ Document uploaded successfully</span>
            <a 
              href={order.astro_analysis_file} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#8B4513', textDecoration: 'underline' }}
            >
              View Document
            </a>
            <button
              onClick={handleRemove}
              disabled={removing}
              style={{
                padding: '8px 16px',
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.8rem',
                background: '#C62828',
                color: '#fff',
                border: 'none',
                cursor: removing ? 'not-allowed' : 'pointer'
              }}
            >
              {removing ? 'Removing...' : '✕ Remove Document'}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {currentStatus === 'not_started' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                  onClick={handleMarkInProgress}
                  disabled={updatingStatus}
                  style={{
                    padding: '8px 16px',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '0.8rem',
                    background: updatingStatus ? '#ccc' : '#1565C0',
                    color: '#fff',
                    border: 'none',
                    cursor: updatingStatus ? 'not-allowed' : 'pointer'
                  }}
                >
                  {updatingStatus ? 'Updating...' : '▶ Mark as "In Progress"'}
                </button>
                <span style={{ fontSize: '0.8rem', color: '#725b47' }}>
                  Click when analysis work has started
                </span>
              </div>
            )}
            
            {currentStatus === 'in_progress' && (
              <div style={{ 
                padding: '12px 16px', 
                background: 'rgba(33,150,243,0.1)', 
                border: '1px solid rgba(33,150,243,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ color: '#1565C0', fontSize: '1.2rem' }}>⏳</span>
                <span style={{ color: '#1565C0', fontWeight: '500' }}>
                  Analysis is in progress. Upload document when ready to mark as "Done".
                </span>
              </div>
            )}
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => setFile(e.target.files[0])}
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
              <button
                onClick={handleUpload}
                disabled={!file || uploading}
                style={{
                  padding: '10px 20px',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.85rem',
                  background: file ? '#2E7D32' : '#ccc',
                  color: '#fff',
                  border: 'none',
                  cursor: file ? 'pointer' : 'not-allowed'
                }}
              >
                {uploading ? 'Uploading...' : '📤 Upload Document'}
              </button>
            </div>
          </div>
        )}
        </div>
      )}
    </div>
  );
};
